import { useEventListener } from "@core/features/eventBus";
import { marketingEventBus, programEventBus, orgInvitationEventBus } from "@platform/events";
import { quizEventBus } from "@core/features/quiz";
import { authEventBus } from "@core/features/auth";
import { useTracking } from "@core/features/tracking";

export function PlatformEventListeners() {
  const { trackEvent } = useTracking();

  // Marketing Events
  useEventListener(
    marketingEventBus,
    "newsletter:submitted",
    ({ email, organizations }) => {
      trackEvent({
        name: "platform_newsletter_submit",
        params: { email, organization: organizations },
      });
    }
  );

  useEventListener(
    marketingEventBus,
    "newsletter:subscribed",
    ({ email, organizations }) => {
      trackEvent({
        name: "newsletter_form_submitted",
        params: { email, organization: organizations },
      });
    }
  );

  useEventListener(
    marketingEventBus,
    "marketing:invitation:direct:viewed",
    ({ invitation }) => {
      trackEvent({
        name: "invitation_direct_viewed",
        params: {
          shortcode: invitation?.shortcode,
          invitation_id: invitation?.id,
          organization_id: invitation?.owner_id,
          organization_name: invitation?.owner_display,
        },
      });
    }
  );

  useEventListener(
    marketingEventBus,
    "marketing:invitation:direct:started",
    ({ invitation }) => {
      trackEvent({
        name: "invitation_direct_join_intent",
        params: {
          shortcode: invitation?.shortcode,
          invitation_id: invitation?.id,
          organization_id: invitation?.owner_id,
          organization_name: invitation?.owner_display,
        },
      });
    }
  );

  // Invitation Events
  useEventListener(orgInvitationEventBus, "lookupOrg:notFound", ({ name }) => {
    trackEvent({
      name: "invitation_org_lookup_notfound",
      params: { organization_name: name },
    });
  });

  useEventListener(
    orgInvitationEventBus,
    "lookupOrg:inviteIntent",
    ({ organization }) => {
      trackEvent({
        name: "invitation_org_lookup_invite_intent",
        params: { organization_name: organization?.organization_name },
      });
    }
  );

  useEventListener(
    orgInvitationEventBus,
    "lookupOrg:joinIntent",
    ({ organization }) => {
      trackEvent({
        name: "invitation_org_lookup_join_intent",
        params: {
          organization_id: organization?.["organization_id"],
          organization_name: organization?.["organization_name"],
        },
      });
    }
  );

  useEventListener(orgInvitationEventBus, "code:submitted", ({ code }) => {
    trackEvent({
      name: "invitation_code_entered",
      params: { code },
    });
  });

  useEventListener(orgInvitationEventBus, "code:success", ({ code }) => {
    trackEvent({
      name: "invitation_code_success",
      params: { code },
    });
  });

  useEventListener(orgInvitationEventBus, "code:failed", ({ code }) => {
    trackEvent({
      name: "invitation_code_failed",
      params: { code },
    });
  });

  // Quiz Events
  // Every quiz event carries the quiz it is about and whatever its setting
  // said of itself, so a report can follow one quiz, or one place a quiz is
  // answered, through all of them.
  useEventListener(quizEventBus, "quiz:loaded", ({ quizId, context }) => {
    trackEvent({
      name: "quiz_loaded",
      params: { quiz_id: quizId, ...context },
    });
  });

  useEventListener(quizEventBus, "quiz:started", ({ quizId, context }) => {
    trackEvent({
      name: "quiz_start",
      params: { quiz_id: quizId, ...context },
    });
  });

  useEventListener(
    quizEventBus,
    "quiz:step",
    ({ quizId, step, stepCount, stepKind, context }) => {
      trackEvent({
        name: "quiz_step",
        params: {
          quiz_id: quizId,
          step_number: step,
          step_count: stepCount,
          step_kind: stepKind,
          ...context,
        },
      });
    }
  );

  useEventListener(quizEventBus, "quiz:halfway", ({ quizId, context }) => {
    trackEvent({
      name: "quiz_halfway",
      params: { quiz_id: quizId, ...context },
    });
  });

  useEventListener(quizEventBus, "quiz:error", ({ quizId, reason, context }) => {
    trackEvent({
      name: "quiz_error",
      params: { quiz_id: quizId, reason, ...context },
    });
  });

  useEventListener(
    quizEventBus,
    "quiz:problem",
    ({ quizId, reason, stepKind, fields, context }) => {
      trackEvent({
        name: "quiz_problem",
        params: {
          quiz_id: quizId,
          reason,
          step_kind: stepKind,
          fields: fields?.join(","),
          ...context,
        },
      });
    }
  );

  useEventListener(
    quizEventBus,
    "quiz:answer:submitted",
    ({ questionId, answer, quizId, responseId }) => {
      trackEvent({
        name: "question_answered",
        params: {
          quiz_id: quizId,
          question_id: questionId,
          answer_value: answer,
          quiz_response_id: responseId,
        },
      });
    }
  );

  useEventListener(
    quizEventBus,
    "quiz:completed",
    ({ quizId, invitationId, responseId, context }) => {
      trackEvent({
        name: "quiz_completed",
        params: {
          quiz_id: quizId,
          invitation_id: invitationId,
          quiz_response_id: responseId,
          ...context,
        },
      });
    }
  );

  // Auth Events
  useEventListener(authEventBus, "auth:login:intent", () => {
    trackEvent({
      name: "login_intent",
      params: {},
    });
  });

  useEventListener(authEventBus, "auth:signup:intent", ({ invitation }) => {
    trackEvent({
      name: "registration_intent",
      params: {
        invitation_code: invitation?.shortcode,
        invitation_id: invitation?.id,
      },
    });
  });

  useEventListener(authEventBus, "auth:signup:started", ({ invitation }) => {
    trackEvent({
      name: "registration_started",
      params: {
        invitation_code: invitation?.shortcode,
        invitation_id: invitation?.id,
      },
    });
  });

  useEventListener(authEventBus, "auth:signup:completed", ({ invitation }) => {
    trackEvent({
      name: "registration_completed",
      params: {
        invitation_code: invitation?.shortcode,
        invitation_id: invitation?.id,
      },
    });
  });

  // Program Events
  useEventListener(
    programEventBus,
    "program:circleModal:open",
    ({ programId, circleId }) => {
      trackEvent({
        name: "open_circle_people_modal",
        params: { program: programId, circle_id: circleId },
      });
    }
  );

  useEventListener(
    programEventBus,
    "program:addToCalendarModal:open",
    ({ programId, circleId }) => {
      trackEvent({
        name: "open_add_to_cal_modal",
        params: { program: programId, circle_id: circleId },
      });
    }
  );

  useEventListener(
    programEventBus,
    "program:addToCalendarModal:addCalendarEvent",
    ({ programId, circleId, meetingId, calendarType }) => {
      trackEvent({
        name: "added_to_cal",
        params: {
          program: programId,
          circle_id: circleId,
          meeting_id: meetingId,
          cal_platform: calendarType,
        },
      });
    }
  );
  return null;
}

export default PlatformEventListeners;
